import { make } from 'vuex-pathify';
import { DateTime } from 'luxon';
import { fetcher, STATE } from './apim-api';
import { extractEntry, DEFAULT_THRESHOLDS } from '../js/browserPerfUtils';

const state = {
  results: {},
  thresholds: { ...DEFAULT_THRESHOLDS },
  status: STATE.NOT_SET,
  error: null,
};

const mutations = {
  ...make.mutations(state),
};

const getters = {
  ...make.getters(state),
  loading: ({ status }) => status === STATE.PENDING,
  loaded: ({ status }) => status === STATE.SUCCESS,
};

const RESULTS_PER_MONITOR = 100;

async function fetchMonitorResults(callId, projectId, from) {
  const response = await fetcher.getResults({
    callId,
    projectId,
    from,
    limit: RESULTS_PER_MONITOR,
  });
  const results = response?.results || response?.data || [];

  // The list endpoint returns synopsis records without CWV fields
  // (largest_contentful_paint, first_contentful_paint, cumulative_layout_shift,
  // first_input_delay, resource_requests). Hydrate each entry via the detail
  // endpoint which returns the full TestResult3 record.
  const fullResults = await Promise.all(
    results.map(async (result) => {
      if (!result?.id) return result;
      try {
        return await fetcher.getResult({ id: result.id, projectId });
      } catch {
        return result;
      }
    }),
  );

  return fullResults.map(extractEntry).filter(Boolean).reverse(); // newest first
}

const actions = {
  async fetchResults({ commit, rootState, rootGetters }, { periodHours = 168 } = {}) {
    const projectId = rootState.account2?.projectId;
    if (!projectId) return;

    commit('SET_STATUS', STATE.PENDING);
    commit('SET_ERROR', null);

    const calls = rootGetters['apimAPI/listCalls'] || [];
    const browserMonitors = calls.filter((c) => c?.meta?.monitor_type === 'www');

    const from = DateTime.now().minus({ hours: periodHours }).toISO();
    const results = {};

    await Promise.allSettled(
      browserMonitors.map(async ({ id: callId }) => {
        try {
          results[callId] = await fetchMonitorResults(callId, projectId, from);
        } catch {
          results[callId] = [];
        }
      }),
    );

    commit('SET_RESULTS', results);
    commit('SET_STATUS', STATE.SUCCESS);
  },

  setThresholds({ commit }, thresholds) {
    commit('SET_THRESHOLDS', thresholds);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
